import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { fetchDocumentResource } from "@/providers/dataProvider/documentDownload";
import { DocumentTransformation, ExtractedRecord, ValidAny } from "@/types";

import { parseWorkbook } from "../utils/parseWorkbook";
import { FieldMapping } from "@/types/transformationTemplate";
import {
  CellHighlightMap,
  RowIndexMode,
  SheetRowIndex,
  buildHeaderColumnMap,
  buildCellHighlightMapForRowRange,
  buildSheetRowIndex,
  maxIndexedSheetRow,
  createColumnResolver,
  findHeaderRowIndex,
  getSheetRowForRecord,
} from "../utils/sourcePreviewUtils";

// =============================================================================
// Configuration
// =============================================================================

/**
 * Controls how line_item_row_index is interpreted.
 * "relative" = offset from header row + 1 (data start)
 * "absolute" = direct index into the sheet (0-indexed from top of xlsx)
 *
 * Confirmed: backend returns absolute row indices.
 */
const ROW_INDEX_MODE: RowIndexMode = "absolute";

// =============================================================================
// Types
// =============================================================================

interface UseSourcePreviewProps {
  documentId: string | undefined;
  transformation: DocumentTransformation | null;
  /** Only fetch the file when true (lazy loading — set true when drawer opens) */
  enabled: boolean;
  /** Template field mappings — used to resolve source columns for orphan errors */
  fieldMappings?: FieldMapping[];
  /**
   * Records to build highlights from (#6755).
   *
   * Passed in rather than read off `transformation.extracted_records`, which is
   * no longer populated on the review tab's fetch. For a large document this is
   * the pages loaded so far, so HIGHLIGHT COVERAGE FOLLOWS LOADED PAGES: the
   * sheet renders in full and scrolls, but a row whose record has not been
   * fetched shows no highlight. That is a deliberate limitation of this step,
   * not a bug — see the note on #6755.
   */
  records?: ExtractedRecord[];
}

interface UseSourcePreviewReturn {
  /** 2D array of cell values from the target sheet */
  sheetData: ValidAny[][] | null;
  /** Column headers (from the detected header row) */
  headers: string[];
  /**
   * Highlights for an inclusive range of sheet rows.
   *
   * Windowed rather than a prebuilt map (#6755): the 497,711-record motivating
   * document produced ~497k highlight objects inside a `useMemo` and froze the
   * tab. The caller asks for the rows it is about to paint.
   */
  highlightsFor: (startRow: number, endRow: number) => CellHighlightMap;
  /** The sheet name being displayed */
  sheetName: string | null;
  /** Header row index in the sheet data */
  headerRowIndex: number;
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Find the spreadsheet row for a specific record */
  getRowForRecord: (record: ExtractedRecord) => number | null;
  /**
   * The highest sheet row the highlight index covers, or null when it covers
   * none (#6900 part B).
   *
   * The index is built from the LOADED records, so on a paged document it
   * stops partway down the sheet. Past that row every cell renders
   * unhighlighted, which reads as "nothing was extracted from these rows"
   * rather than "these records are not loaded" — a false statement about the
   * extraction, made by omission.
   *
   * Exposed so the surface can say which it is. The alternative — fetching the
   * records for a scrolled row range — is a bigger change, and the honest
   * disclosure is what #6900 AC2 actually asks for ("either the highlights are
   * present, or the surface says the rows are not loaded").
   */
  highlightedThroughRow: number | null;
}

// =============================================================================
// Hook
// =============================================================================

export const useSourcePreview = ({
  documentId,
  transformation,
  enabled,
  fieldMappings,
  records,
}: UseSourcePreviewProps): UseSourcePreviewReturn => {
  const [sheetData, setSheetData] = useState<ValidAny[][] | null>(null);
  const [sheetName, setSheetName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache the parsed workbook so we only download + parse once
  const cacheRef = useRef<{
    documentId: string;
    sheetData: ValidAny[][];
    sheetName: string;
  } | null>(null);

  // Fetch and parse the file
  useEffect(() => {
    if (!enabled || !documentId) return;

    // Use cache if available for this document
    if (cacheRef.current?.documentId === documentId) {
      setSheetData(cacheRef.current.sheetData);
      setSheetName(cacheRef.current.sheetName);
      return;
    }

    const abortController = new AbortController();

    const fetchAndParse = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const apiUrl = getPartnerAwareApiUrl();
        const url = `${apiUrl}/documents/${documentId}/download`;

        // Partner-scoped, authenticated headers are built centrally so this raw
        // download can't silently drop X-Partner-ID (#5350).
        const response = await fetchDocumentResource(url, { signal: abortController.signal });

        const arrayBuffer = await response.arrayBuffer();

        if (abortController.signal.aborted) return;

        // Parsed off the main thread (#6892). XLSX.read is synchronous and
        // takes 3.84 s on the 12.9 MB / 497,711-row fixture — measured with
        // ZERO frames rendered for the 4.35 s the whole parse took, so the
        // spinner froze mid-animation and the drawer could not be closed.
        //
        // Falls back to parsing inline where a worker cannot run (jsdom, or a
        // CSP that blocks module workers): the old frozen-tab behaviour,
        // which is worse than the worker and better than no preview.
        const parsed = await parseWorkbook(
          arrayBuffer,
          transformation?.processing_metadata?.process_sheet_name,
          abortController.signal
        );

        if (abortController.signal.aborted) return;

        const targetSheetName = parsed.sheetName;
        const data = parsed.rows as ValidAny[][];

        // Cache and set state
        cacheRef.current = { documentId, sheetData: data, sheetName: targetSheetName };
        setSheetData(data);
        setSheetName(targetSheetName);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("[useSourcePreview] Error:", err);
        setError(err instanceof Error ? err.message : "Failed to load spreadsheet");
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    void fetchAndParse();

    return () => {
      abortController.abort();
    };
  }, [enabled, documentId, transformation?.processing_metadata?.process_sheet_name]);

  // Detect the header row
  const detectedHeaders = transformation?.processing_metadata?.detected_headers;

  const headerRowIndex = useMemo(() => {
    if (!sheetData) return 0;
    return findHeaderRowIndex(sheetData, detectedHeaders || []);
  }, [sheetData, detectedHeaders]);

  // Build the header column map
  const headerColumnMap = useMemo(() => {
    if (!sheetData || sheetData.length === 0) return new Map<string, number>();
    const headerRow = sheetData[headerRowIndex] || [];
    return buildHeaderColumnMap(headerRow, detectedHeaders);
  }, [sheetData, headerRowIndex, detectedHeaders]);

  // Extract headers for display
  const headers = useMemo(() => {
    if (!sheetData || sheetData.length === 0) return [];
    const headerRow = sheetData[headerRowIndex] || [];
    return headerRow.map((cell: ValidAny) => (cell !== null && cell !== undefined ? String(cell) : ""));
  }, [sheetData, headerRowIndex]);

  // Build reverse map: target_field → source header key
  // Used to resolve source columns for "orphan" errors — fields that failed extraction
  // entirely and have no ExtractedField entry (e.g. "claimant_birth_date" → "Age Band")
  //
  // Template `field_mappings` is the ONLY source (#6755 AC3). It previously seeded
  // from `extracted_records` first and let the template overwrite, which was both
  // redundant and strictly worse:
  //
  //  - The record pass only ever kept the FIRST occurrence of each target_field,
  //    then the template overwrote every key it also covered. Its whole
  //    contribution was target_fields the template does not mention.
  //  - It cannot cover the case this map exists for. An orphan error is a field
  //    that failed extraction, which by definition produces no ExtractedField —
  //    so the records fallback is blank exactly when it is needed.
  //  - It walked the entire record set to do it, which is what this story removes.
  const targetToSourceMap = useMemo(() => {
    if (!fieldMappings) return undefined;

    const map = new Map<string, string>();
    for (const fm of fieldMappings) {
      if (fm.target_field && fm.extraction_location?.key) {
        map.set(fm.target_field, fm.extraction_location.key);
      }
    }

    return map.size > 0 ? map : undefined;
  }, [fieldMappings]);

  // Index records by sheet row — the one full pass over the record set.
  // Cheap next to building highlights: a pointer write per record rather than a
  // CellHighlight object, two arrays and a map entry per cell.
  const rowIndex = useMemo<SheetRowIndex>(() => {
    if (!sheetData || !records) return new Map();
    return buildSheetRowIndex(records, headerRowIndex, sheetData.length, ROW_INDEX_MODE);
  }, [sheetData, headerRowIndex, records]);

  // One resolver across all windows, so the source-key → column cache survives
  // scrolling instead of being rebuilt per frame.
  const resolveColumn = useMemo(() => createColumnResolver(headerColumnMap), [headerColumnMap]);

  const highlightsFor = useCallback(
    (startRow: number, endRow: number): CellHighlightMap => {
      if (!sheetData) return new Map() as CellHighlightMap;
      return buildCellHighlightMapForRowRange(
        sheetData,
        rowIndex,
        resolveColumn,
        headerColumnMap,
        startRow,
        endRow,
        targetToSourceMap
      );
    },
    [sheetData, rowIndex, resolveColumn, headerColumnMap, targetToSourceMap]
  );

  // Derived from the SAME index highlightsFor reads, so the disclosure and the
  // highlighting cannot disagree about where coverage ends. Computing it from
  // the record list instead would be a second answer to one question.
  const highlightedThroughRow = useMemo(() => maxIndexedSheetRow(rowIndex), [rowIndex]);

  // Row lookup for scroll-into-view
  const getRowForRecord = useCallback(
    (record: ExtractedRecord): number | null => {
      return getSheetRowForRecord(record, headerRowIndex, ROW_INDEX_MODE);
    },
    [headerRowIndex]
  );

  return {
    sheetData,
    headers,
    highlightsFor,
    highlightedThroughRow,
    sheetName,
    headerRowIndex,
    isLoading,
    error,
    getRowForRecord,
  };
};

// =============================================================================
// Helpers
// =============================================================================

/** Resolve the target sheet name from the workbook */
